/**
 * Indicators Hook
 * Reusable hook for formatting and handling sustainability indicators
 */

import { useMemo } from 'react';
import { DashboardData } from '../types/api';
import {
  formatNumberEs,
  formatDelta,
  convertWaterToLiters,
  WATER_UNIT,
} from '../utils/formatters';

export interface FormattedIndicator {
  id: string;
  title: string;
  value: string;
  unit: string;
  delta: string | undefined;
  deltaValue?: number | null;
  iconName: string;
  showMissingDataWarning?: boolean;
  missingDataMessage?: string;
  tooltipMessage?: string | undefined; // New field for detailed tooltip information
}

export interface UseIndicatorsResult {
  indicators: FormattedIndicator[];
  hasServiceVolumeData: boolean;
  currentDishes: number | undefined;
}

/**
 * Hook to format dashboard indicators with proper per-dish calculations
 * and fallback to totals when service volume data is missing
 */
export function useIndicators(
  dashboardData?: DashboardData,
): UseIndicatorsResult {
  return useMemo(() => {
    if (!dashboardData) {
      return {
        indicators: [],
        hasServiceVolumeData: false,
        currentDishes: undefined,
      };
    }

    const { metrics, deltas, raw_totals, current_dishes, data_available } =
      dashboardData;
    const hasServiceVolumeData = metrics.has_covers_volume_data;

    // Helper function to generate tooltip messages for missing data
    const generateTooltipMessage = (
      indicatorId: string,
    ): string | undefined => {
      if (!data_available) return undefined;

      const { last_month, previous_month } = data_available;

      // Check if data is missing for this indicator
      const hasDataForIndicator = (
        monthData: any,
        indicatorId: string,
      ): boolean => {
        switch (indicatorId) {
          case 'energy':
          case 'water':
          case 'gas':
            return monthData.has_consumption_data;
          case 'waste_diversion':
            return monthData.has_waste_data;
          default:
            return true; // CO2e and suppliers don't depend on monthly data
        }
      };

      const lastMonthHasData = hasDataForIndicator(last_month, indicatorId);
      const previousMonthHasData = hasDataForIndicator(
        previous_month,
        indicatorId,
      );

      if (!lastMonthHasData && !previousMonthHasData) {
        return `No hay datos disponibles para ${last_month.month_name} ni ${previous_month.month_name}`;
      } else if (!lastMonthHasData) {
        return `No hay datos disponibles para ${last_month.month_name}`;
      } else if (!previousMonthHasData) {
        return `No hay datos disponibles para ${previous_month.month_name}`;
      }

      return undefined;
    };

    const indicators: FormattedIndicator[] = [
      {
        id: 'energy',
        title: hasServiceVolumeData
          ? 'Energía por plato'
          : 'Consumo de energía',
        value: formatNumberEs(
          hasServiceVolumeData
            ? (metrics.energia_kwh_per_dish ?? 0)
            : raw_totals?.energia_kwh || 0,
          hasServiceVolumeData ? 3 : 1,
        ),
        unit: hasServiceVolumeData ? 'kWh/plato' : 'kWh',
        delta: formatDelta(deltas.energia_kwh_per_dish),
        deltaValue: deltas.energia_kwh_per_dish,
        iconName: 'energy' as const,
        showMissingDataWarning:
          !hasServiceVolumeData && (raw_totals?.energia_kwh || 0) > 0,
        missingDataMessage: 'Datos de ventas faltantes',
        tooltipMessage: generateTooltipMessage('energy'),
      },
      {
        id: 'water',
        title: hasServiceVolumeData ? 'Agua por plato' : 'Consumo de agua',
        value: formatNumberEs(
          convertWaterToLiters(
            hasServiceVolumeData
              ? (metrics.agua_m3_per_dish ?? 0)
              : raw_totals?.agua_m3 || 0,
          ) ?? 0,
          hasServiceVolumeData ? 1 : 0,
        ),
        unit: hasServiceVolumeData ? WATER_UNIT.PER_DISH : WATER_UNIT.TOTAL,
        delta: formatDelta(deltas.agua_m3_per_dish),
        deltaValue: deltas.agua_m3_per_dish,
        iconName: 'water' as const,
        showMissingDataWarning:
          !hasServiceVolumeData && (raw_totals?.agua_m3 || 0) > 0,
        missingDataMessage: 'Datos de ventas faltantes',
        tooltipMessage: generateTooltipMessage('water'),
      },
      {
        id: 'gas',
        title: hasServiceVolumeData ? 'Gas por plato' : 'Consumo de gas',
        value: formatNumberEs(
          hasServiceVolumeData
            ? (metrics.gas_m3_per_dish ?? 0)
            : raw_totals?.gas_m3 || 0,
          hasServiceVolumeData ? 3 : 1,
        ),
        unit: hasServiceVolumeData ? 'm³/plato' : 'm³',
        delta: formatDelta(deltas.gas_m3_per_dish),
        deltaValue: deltas.gas_m3_per_dish,
        iconName: 'gas' as const,
        showMissingDataWarning:
          !hasServiceVolumeData && (raw_totals?.gas_m3 || 0) > 0,
        missingDataMessage: 'Datos de ventas faltantes',
        tooltipMessage: generateTooltipMessage('gas'),
      },
      {
        id: 'waste_diversion',
        title: 'Residuos desviados',
        value: formatNumberEs(metrics.waste_diversion_pct, 1),
        unit: '%',
        delta: formatDelta(deltas.waste_diversion_pct),
        deltaValue: deltas.waste_diversion_pct,
        iconName: 'waste' as const,
        showMissingDataWarning: false,
        tooltipMessage: generateTooltipMessage('waste_diversion'),
      },
      {
        id: 'co2e',
        title: hasServiceVolumeData ? 'CO₂e por plato' : 'CO₂e total',
        value: formatNumberEs(
          hasServiceVolumeData
            ? (metrics.co2e_per_dish ?? 0)
            : (raw_totals?.co2e_kg ?? 0),
          1,
        ),
        unit: hasServiceVolumeData ? 'kg CO2e/plato' : 'kg CO2e',
        delta: formatDelta(deltas.co2e_per_dish),
        deltaValue: deltas.co2e_per_dish,
        iconName: 'eco' as const,
        showMissingDataWarning: false,
        missingDataMessage: 'En desarrollo',
        tooltipMessage: generateTooltipMessage('co2e'),
      },
      {
        id: 'suppliers',
        title: 'Proveedores certificados',
        value: formatNumberEs(metrics.supplier_certification_pct, 0),
        unit: '%',
        delta: undefined, // No month-over-month comparison for supplier certification
        iconName: 'suppliers' as const,
        showMissingDataWarning: false,
        tooltipMessage: generateTooltipMessage('suppliers'),
      },
    ];

    for (const indicator of indicators) {
      const current = data_available?.last_month;
      const missing =
        current &&
        (['energy', 'water', 'gas'].includes(indicator.id)
          ? !current.has_consumption_data
          : indicator.id === 'waste_diversion'
            ? !current.has_waste_data
            : false);
      if (missing) {
        indicator.value = '—';
        indicator.delta = undefined;
        indicator.deltaValue = null;
      }
    }

    return {
      indicators,
      hasServiceVolumeData,
      currentDishes: current_dishes,
    };
  }, [dashboardData]);
}

/**
 * Get specific indicator by ID for individual component usage
 */
export function useIndicator(
  dashboardData: DashboardData | undefined,
  indicatorId: string,
): FormattedIndicator | undefined {
  const { indicators } = useIndicators(dashboardData);
  return indicators.find((indicator) => indicator.id === indicatorId);
}

/**
 * Hook to format indicator values for different contexts
 */
export function useIndicatorFormatters() {
  return useMemo(
    () => ({
      /**
       * Format energy consumption per dish or total
       */
      formatEnergyIndicator: (
        value: number,
        hasServiceVolume: boolean,
        decimals = 3,
      ) => ({
        value: formatNumberEs(value, hasServiceVolume ? decimals : 1),
        unit: hasServiceVolume ? 'kWh/plato' : 'kWh',
        title: hasServiceVolume ? 'Energía por plato' : 'Consumo de energía',
      }),

      /**
       * Format water consumption per dish or total
       */
      formatWaterIndicator: (
        value: number,
        hasServiceVolume: boolean,
        decimals = 3,
      ) => ({
        value: formatNumberEs(
          convertWaterToLiters(value) ?? 0,
          hasServiceVolume ? decimals : 0,
        ),
        unit: hasServiceVolume ? WATER_UNIT.PER_DISH : WATER_UNIT.TOTAL,
        title: hasServiceVolume ? 'Agua por plato' : 'Consumo de agua',
      }),

      /**
       * Format gas consumption per dish or total
       */
      formatGasIndicator: (
        value: number,
        hasServiceVolume: boolean,
        decimals = 3,
      ) => ({
        value: formatNumberEs(value, hasServiceVolume ? decimals : 1),
        unit: hasServiceVolume ? 'm³/plato' : 'm³',
        title: hasServiceVolume ? 'Gas por plato' : 'Consumo de gas',
      }),

      /**
       * Format waste diversion percentage
       */
      formatWasteIndicator: (value: number) => ({
        value: formatNumberEs(value, 1),
        unit: '%',
        title: 'Residuos desviados',
      }),

      /**
       * Format CO2e per dish
       */
      formatCO2eIndicator: (value: number, hasServiceVolume: boolean) => ({
        value: formatNumberEs(value, 1),
        unit: hasServiceVolume ? 'kg CO2e/plato' : 'kg CO2e',
        title: hasServiceVolume ? 'CO₂e por plato' : 'CO₂e total',
      }),

      /**
       * Format supplier certification percentage
       */
      formatSupplierIndicator: (value: number) => ({
        value: formatNumberEs(value, 0),
        unit: '%',
        title: 'Proveedores certificados',
      }),
    }),
    [],
  );
}

/**
 * Utility function to determine if an indicator should show missing data warning
 */
export function shouldShowMissingDataWarning(
  hasServiceVolume: boolean,
  rawValue: number,
): boolean {
  return !hasServiceVolume && rawValue > 0;
}

/**
 * Get missing data message for indicators
 */
export function getMissingDataMessage(
  indicatorType: 'energy' | 'water' | 'gas' | 'co2e',
): string {
  switch (indicatorType) {
    case 'co2e':
      return 'En desarrollo';
    default:
      return 'Datos de ventas faltantes';
  }
}
